import { collection, doc, getDoc, getDocs, increment, query, runTransaction, serverTimestamp, where, type Firestore } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { uploadWorkspaceArtifact } from './storageHubService'
import type { FormDefinition, FormResponse, FormUploadedFile } from '../types/forms'
import { logAuditEvent } from './auditLedgerService'
import { assertWorkspaceQuota } from './quotaGuardService'
import { normalizeTierId } from '../config/PricingConfig'

import { validateResponsePayload } from '../utils/forms/formAnswerValidator'
export { validateResponsePayload }

type UploadArtifact = typeof uploadWorkspaceArtifact

const uploadResponseFiles = async (workspaceId: string, responseId: string, definition: FormDefinition, values: Record<string, unknown>, uploadArtifact: UploadArtifact) => {
  const data: Record<string, unknown> = {}
  for (const field of definition.fields) {
    const value = values[field.id]
    if (field.type !== 'FILE_UPLOAD') { data[field.id] = value ?? null; continue }
    const files = Array.isArray(value) ? value.filter((entry): entry is File => entry instanceof File) : []
    const uploaded: FormUploadedFile[] = []
    for (const file of files) {
      const result = await uploadArtifact({ workspaceId, file, visibility: 'WORKSPACE_SHARED', artifactType: 'FORM_RESPONSE_FILE', artifactId: responseId })
      uploaded.push({ fileId: result.fileId, fileName: file.name, sizeBytes: file.size, mimeType: file.type || 'application/octet-stream', downloadUrl: result.downloadUrl, storagePath: result.storagePath })
    }
    data[field.id] = uploaded
  }
  return data
}

export const submitFormResponse = async (
  input: { definition: FormDefinition; values: Record<string, unknown>; userId: string; userName: string; helperUids?: ReadonlySet<string> },
  dependencies: { db?: Firestore; uploadArtifact?: UploadArtifact; checkQuota?: typeof assertWorkspaceQuota } = {},
) => {
  const db = dependencies.db ?? firestoreDb
  const uploadArtifact = dependencies.uploadArtifact ?? uploadWorkspaceArtifact
  const errors = validateResponsePayload(input.definition, input.values, { helperUids: input.helperUids })
  if (Object.keys(errors).length > 0) return { response: null, errors }
  if (input.definition.lifecycleStatus !== 'ACTIVE' || input.definition.isLocked) throw new Error('This form is not accepting responses.')
  const deadline = input.definition.deadline instanceof Date ? input.definition.deadline : input.definition.deadline?.toDate()
  if (deadline && deadline.getTime() < Date.now()) throw new Error('This form has passed its deadline.')
  if (input.definition.fields.some((field) => field.type === 'FILE_UPLOAD')) {
    const workspaceSnapshot = await getDoc(doc(db, 'workspaces', input.definition.workspaceId))
    if (normalizeTierId(workspaceSnapshot.data()?.plan) === 'FREE_TIER') throw new Error('Free workspaces cannot accept form file uploads.')
  }
  await (dependencies.checkQuota ?? assertWorkspaceQuota)(input.definition.workspaceId, 'FORM_RESPONSE')

  const responseRef = doc(collection(db, 'form_responses'))
  const data = await uploadResponseFiles(input.definition.workspaceId, responseRef.id, input.definition, input.values, uploadArtifact)
  const pendingRequests = await getDocs(query(collection(db, 'form_requests'), where('workspaceId', '==', input.definition.workspaceId), where('formId', '==', input.definition.id), where('recipientId', '==', input.userId), where('status', '==', 'PENDING')))
  const sourceItems = pendingRequests.docs.flatMap((request) => {
    const value = request.data().sourceItems
    return Array.isArray(value) ? value : []
  }).filter((source, index, all): source is NonNullable<FormResponse['sourceItems']>[number] =>
    source
    && typeof source === 'object'
    && typeof source.itemId === 'string'
    && typeof source.itemType === 'string'
    && typeof source.itemTitle === 'string'
    && all.findIndex((entry) => entry.itemId === source.itemId) === index,
  )
  const formRef = doc(db, 'form_definitions', input.definition.id)
  await runTransaction(db, async (transaction) => {
    const [formSnapshot, workspace] = await Promise.all([transaction.get(formRef), transaction.get(doc(db, 'workspaces', input.definition.workspaceId))])
    if (!formSnapshot.exists()) throw new Error('Form definition no longer exists.')
    const live = formSnapshot.data()
    if (live.lifecycleStatus !== 'ACTIVE' || live.isLocked === true) throw new Error('This form is no longer accepting responses.')
    transaction.set(responseRef, { workspaceId: input.definition.workspaceId, formId: input.definition.id, submittedBy: input.userId, submittedByName: input.userName, data, status: 'PENDING', sourceItems, submittedAt: serverTimestamp() })
    transaction.update(formRef, { submissionCount: increment(1), lastSubmissionResponseId: responseRef.id })
    pendingRequests.docs.forEach((request) => transaction.update(request.ref, { status: 'COMPLETED', responseId: responseRef.id, completedAt: serverTimestamp() }))
    logAuditEvent(db, transaction, workspace.data(), { workspaceId: input.definition.workspaceId, actorId: input.userId, actorName: input.userName, nodeType: 'FORM_RESPONSE', targetId: responseRef.id, targetTitle: input.definition.title, action: 'FORM_SUBMITTED', actionType: 'SUBMIT', activityType: 'CREATE', summary: `Submitted response to ${input.definition.title}`, payload: { formId: input.definition.id, completedRequestIds: pendingRequests.docs.map((entry) => entry.id) } })
  })
  emitFormEconomyAction(input.definition.workspaceId, input.userId)
  void triggerPostSubmissionWebhook(responseRef.id).catch((error) => console.warn('[Forms] post-submission webhook failed', error))
  const response: FormResponse = { id: responseRef.id, workspaceId: input.definition.workspaceId, formId: input.definition.id, submittedBy: input.userId, submittedByName: input.userName, data, status: 'PENDING', sourceItems, submittedAt: null }
  return { response, errors: {} }
}

const triggerPostSubmissionWebhook = async (responseId: string) => {
  void responseId
  // TODO: Dispatch a signed webhook to Zapier/Make through trusted backend infrastructure.
}

const emitFormEconomyAction = (workspaceId: string, actorId: string) => {
  void workspaceId
  void actorId
  // TODO: Bridge FORM_SUBMITTED into AppEconomyLoop after the reward policy is ratified.
}
